import { LOCALSTORAGE_TEAM_SELECTED_KEY } from "@/contexts/teams-provider";
import { useTranslation } from "@/hooks/useTranslation";
import axios from "axios";
import Cookies from "js-cookie";
import posthog from "posthog-js";
import { useMemo } from "react";

export const getBearerToken = () => {
  return Cookies.get("__session") || "";
};

/**
 * The user's IANA time zone, sent on every request so the API can date an
 * answer on the clock the user is actually reading it on rather than on the
 * server's UTC one.
 */
const getTimeZone = () =>
  Intl.DateTimeFormat().resolvedOptions().timeZone || "";

export const useApiRequest = () => {
  const { currentLanguage } = useTranslation();

  return {
    url: (path: string) => `${import.meta.env.VITE_API_BASE_URL}${path}`,
    headers: () => ({
      Authorization: `Bearer ${getBearerToken()}`,
      "Team-Id": localStorage.getItem(LOCALSTORAGE_TEAM_SELECTED_KEY) || "",
      "Accept-Language": currentLanguage,
      "Time-Zone": getTimeZone(),
      "X-PostHog-Session-Id": posthog.get_session_id?.() || "",
      "X-PostHog-Distinct-Id": posthog.get_distinct_id?.() || "",
    }),
  };
};

export const useApi = () => {
  const { currentLanguage } = useTranslation();

  return useMemo(() => {
    const instance = axios.create({
      baseURL: import.meta.env.VITE_API_BASE_URL,
    });

    instance.interceptors.request.use((config) => {
      config.headers["Team-Id"] =
        localStorage.getItem(LOCALSTORAGE_TEAM_SELECTED_KEY) || "";
      config.headers["Accept-Language"] = currentLanguage;
      config.headers["Time-Zone"] = getTimeZone();
      config.headers["X-PostHog-Session-Id"] =
        posthog.get_session_id?.() || "";
      config.headers["X-PostHog-Distinct-Id"] =
        posthog.get_distinct_id?.() || "";
      const token = getBearerToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    return instance;
  }, [currentLanguage]);
};

export type SSEStreamCallbacks = {
  onOpen?: (abortController: AbortController) => void;
  onMessage: (data: { data: string }) => void;
  onClose?: () => void;
  onError?: (error: Error) => void;
};

/**
 * Error thrown when an SSE request fails before/while streaming. Carries the
 * HTTP `status` and, when the server returned a JSON error body, its `code`
 * so callers can branch on specific failures (e.g. quota limits).
 */
export type SSEError = Error & {
  status?: number;
  code?: string;
};

/**
 * Parse SSE events from a ReadableStream.
 * Handles the `data: {json}\n\n` format used by NestJS @Sse().
 */
export async function consumeSSEStream(
  response: Response,
  callbacks: SSEStreamCallbacks,
  signal: AbortSignal
): Promise<void> {
  const reader = response.body!.pipeThrough(new TextDecoderStream()).getReader();
  let buffer = "";

  try {
    while (!signal.aborted) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += value;
      const chunks = buffer.split("\n\n");
      buffer = chunks.pop() ?? "";

      for (const chunk of chunks) {
        const dataLine = chunk
          .split("\n")
          .find((line) => line.startsWith("data: "));
        if (dataLine) {
          callbacks.onMessage({ data: dataLine.slice(6) });
        }
      }
    }
  } finally {
    reader.releaseLock();
  }
}

/** The team every request is sent for, as its `Team-Id` header carries it. */
export const getSelectedTeamId = (): string =>
  localStorage.getItem(LOCALSTORAGE_TEAM_SELECTED_KEY) || "";

export const useApiStream = () => {
  const { currentLanguage } = useTranslation();

  const getHeaders = (): Record<string, string> => ({
    Accept: "text/event-stream",
    "Content-Type": "application/json",
    Authorization: `Bearer ${getBearerToken()}`,
    "Team-Id": getSelectedTeamId(),
    "Accept-Language": currentLanguage,
    "Time-Zone": getTimeZone(),
    "X-PostHog-Session-Id": posthog.get_session_id?.() || "",
    "X-PostHog-Distinct-Id": posthog.get_distinct_id?.() || "",
  });

  const fetchSSE = async (
    url: string,
    init: RequestInit,
    callbacks: SSEStreamCallbacks
  ): Promise<void> => {
    const abortController = new AbortController();
    const { signal } = abortController;

    try {
      const response = await fetch(url, {
        ...init,
        headers: { ...getHeaders(), ...(init.headers as Record<string, string>) },
        signal,
      });

      if (!response.ok) {
        let code: string | undefined;
        try {
          const body = await response.json();
          if (typeof body?.code === "string") code = body.code;
        } catch {
          // Non-JSON or empty error body — fall back to a status-only error.
        }
        const error: SSEError = new Error(
          `SSE error ${response.status}: ${response.statusText || "Unknown"}`
        );
        error.status = response.status;
        if (code) error.code = code;
        throw error;
      }

      callbacks.onOpen?.(abortController);

      await consumeSSEStream(response, callbacks, signal);
      callbacks.onClose?.();
    } catch (error) {
      if (signal.aborted) return;
      callbacks.onError?.(error as Error);
    }
  };

  return {
    /**
     * The team the streams are sent for: a store that caches what they answer
     * keys it by this team, so a team switch never serves another team's.
     */
    teamId: getSelectedTeamId(),
    get: async (uri: string, callbacks: SSEStreamCallbacks) =>
      fetchSSE(`${import.meta.env.VITE_API_BASE_URL}${uri}`, {}, callbacks),

    post: async (
      uri: string,
      data: Record<string, unknown>,
      callbacks: SSEStreamCallbacks
    ) =>
      fetchSSE(
        `${import.meta.env.VITE_API_BASE_URL}${uri}`,
        { method: "POST", body: JSON.stringify(data) },
        callbacks
      ),
  };
};
